import { Box, Button, Typography, useTheme } from "@mui/material";

interface SubmissionsEmptyStateProps {
  timeFilter: string;
  readyForAction: boolean;
  isStaff: boolean;
  onCreateSubmission: () => void;
  onViewUpcoming: () => void;
  omnisearch: string;
  onClearSearch: () => void;
}

export const SubmissionsEmptyState = ({
  timeFilter,
  readyForAction,
  isStaff,
  onCreateSubmission,
  onViewUpcoming,
  omnisearch,
  onClearSearch,
}: SubmissionsEmptyStateProps) => {
  const theme = useTheme();

  // Common container styles matching Figma design
  const containerStyles = {
    position: "relative" as const,
    height: "400px",
    overflow: "hidden",
    bgcolor: theme.palette.grey50.main,
    borderRadius: "4px",
    mx: 3, // 24px horizontal margin to match Paper padding
    mb: 3, // 24px bottom margin to match Paper padding
  };

  // Common text styles matching Figma design
  const textStyles = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.15px",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap" as const,
  };

  // Handle "All Submissions" view (no specific time filter)
  if (timeFilter === "" || timeFilter === "all") {
    return (
      <Box sx={containerStyles}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: "0.15px",
              color: theme.palette.text.secondary,
              mb: omnisearch ? 2 : 0,
            }}
          >
            {omnisearch
              ? `📄 No submissions to show for search query: "${omnisearch}"`
              : "📄 No submissions to show"}
          </Typography>
          {omnisearch && (
            <Button variant="outlined" size="small" onClick={onClearSearch} sx={{ mt: 0 }}>
              Clear Search
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (timeFilter === "upcoming") {
    return (
      <Box sx={containerStyles}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: "0.15px",
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            {omnisearch
              ? `🔶 No upcoming submissions to show for search query: "${omnisearch}"`
              : "🔶 No upcoming submissions to show"}
          </Typography>
          {isStaff && (
            <Typography
              component="span"
              onClick={onCreateSubmission}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "24px",
                letterSpacing: "0.4px",
                color: theme.palette.secondary.main,
                textTransform: "uppercase",
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Create Submission
            </Typography>
          )}
          {omnisearch && (
            <Button
              variant="outlined"
              size="small"
              onClick={onClearSearch}
              sx={{ mt: 2, display: "block", mx: "auto" }}
            >
              Clear Search
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (timeFilter === "in-progress") {
    return (
      <Box sx={containerStyles}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: "0.15px",
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            {omnisearch
              ? `💬 Nothing currently in-progress for search query: "${omnisearch}"`
              : "💬 Nothing currently in-progress"}
          </Typography>
          {isStaff && (
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: "0.15px",
                color: theme.palette.text.secondary,
              }}
            >
              View{" "}
              <Typography
                component="span"
                onClick={onViewUpcoming}
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  letterSpacing: "0.15px",
                  color: theme.palette.text.secondary,
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                Upcoming Submissions
              </Typography>{" "}
              to send a submission to reviewers
            </Typography>
          )}
          {omnisearch && (
            <Button variant="outlined" size="small" onClick={onClearSearch} sx={{ mt: 2 }}>
              Clear Search
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (timeFilter === "completed") {
    return (
      <Box data-testid="submissions-empty-state-completed" sx={containerStyles}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: "0.15px",
              color: theme.palette.text.secondary,
              mb: omnisearch ? 2 : 0,
            }}
          >
            {omnisearch
              ? `🏝️ No completed submissions to show for search query: "${omnisearch}"`
              : "🏝️ No completed submissions to show"}
          </Typography>
          {omnisearch && (
            <Button variant="outlined" size="small" onClick={onClearSearch} sx={{ mt: 0 }}>
              Clear Search
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (readyForAction) {
    return (
      <Box sx={containerStyles}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: "0.15px",
              color: theme.palette.text.secondary,
              mb: omnisearch ? 2 : 0,
            }}
          >
            {omnisearch
              ? `👤 No submissions ready for your next action for search query: "${omnisearch}"`
              : "👤 No submissions ready for your next action"}
          </Typography>
          {omnisearch && (
            <Button variant="outlined" size="small" onClick={onClearSearch} sx={{ mt: 0 }}>
              Clear Search
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Default fallback
  return (
    <Box sx={containerStyles}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: "0.15px",
            color: theme.palette.text.secondary,
            mb: omnisearch ? 2 : 0,
          }}
        >
          {omnisearch
            ? `📄 No submissions to show for search query: "${omnisearch}"`
            : "📄 No submissions to show"}
        </Typography>
        {omnisearch && (
          <Button variant="outlined" size="small" onClick={onClearSearch} sx={{ mt: 0 }}>
            Clear Search
          </Button>
        )}
      </Box>
    </Box>
  );
};
